/**
 * "Whose plate is it on" — the NCR list's process columns, progress strip and
 * Next Action cell (spec, 24 Sep 2026).
 *
 * Everything here renders server-computed lifecycle fields
 * (`process_plate`, `next_actions`, `ncr_state` from
 * `quality/services/ncr_lifecycle.py`). The same functions guard the workflow
 * and drive the Awaiting chips and the Daily Summary email, so a row can never
 * say "Awaiting Customer" while the counts or the Close button disagree.
 */
import type { NCRPlateCell, NCRDaysWaiting } from '../../lib/api/types/quality';
import { cn } from '../../lib/utils';

export type PlateKey = 'eightd' | 'collection' | 'credit' | 'replacement';

export const PLATE_LABELS: Record<PlateKey, string> = {
  eightd: '8D',
  collection: 'Collection',
  credit: 'Credit',
  replacement: 'Replacement',
};

/** The owner tag: S = the supplier acts next, C = the customer does. */
export function OwnerMark({ owner }: { owner: 'S' | 'C' | null | undefined }) {
  if (!owner) return null;
  const supplier = owner === 'S';
  return (
    <span
      className={cn(
        'ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded px-1 text-[10px] font-bold leading-none',
        supplier ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800',
      )}
      title={supplier ? 'Supplier to act' : 'Customer to act'}
      aria-label={supplier ? 'Supplier to act' : 'Customer to act'}
    >
      {owner}
    </span>
  );
}

/** One process column cell: its sub-status plus the owner tag. */
export function ProcessCell({ cell }: { cell: NCRPlateCell | undefined }) {
  if (!cell) return <span className="text-muted-foreground/50">&mdash;</span>;
  const notRequired = cell.label === 'Not required';
  const done = !!cell.done;
  return (
    <span className="inline-flex items-center whitespace-nowrap">
      <span
        className={cn(
          notRequired && 'text-muted-foreground',
          done && 'font-medium text-emerald-700',
        )}
      >
        {cell.label}
      </span>
      <OwnerMark owner={cell.owner} />
    </span>
  );
}

/** Top-level NCR state (spec: Open / Closed / Deleted). */
export function NCRStateBadge({ state, readyToClose = false }: { state: string | undefined; readyToClose?: boolean }) {
  const s = state || 'Open';
  // T7 (30 Sep 2026): with the Progress / Next Action columns gone, the
  // customer's last step -- closing it -- shows on Status.
  if (s === 'Open' && readyToClose) {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded border border-[#5b8fd1] bg-[#e8f1fb] px-2 py-1 text-xs font-medium text-[#1b4f8a]">
        Ready to close<OwnerMark owner="C" />
      </span>
    );
  }
  const cls =
    s === 'Closed'
      ? 'bg-[#d4edda] text-[#155724] border-[#28a745]'
      : s === 'Deleted'
        ? 'bg-[#f8d7da] text-[#721c24] border-[#dc3545]'
        : s === 'Draft'
          ? 'bg-white text-[#6c757d] border-dashed border-[#adb5bd]'
          : 'bg-[#e8f1fb] text-[#1b4f8a] border-[#5b8fd1]';
  return (
    <span className={`inline-block rounded border px-2 py-1 text-xs font-medium ${cls}`}>
      {s}
    </span>
  );
}

/** One-line legend shown above the list (spec: "(S)/(C) legend above the grid"). */
export function PlateLegend() {
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="inline-flex items-center">
        <OwnerMark owner="S" />
        <span className="ml-1">Supplier to act</span>
      </span>
      <span className="inline-flex items-center">
        <OwnerMark owner="C" />
        <span className="ml-1">Customer to act</span>
      </span>
    </div>
  );
}

/** "12 days waiting" while open, "took 5 days" once done (legacy counters). */
export function daysWaitingText(d: NCRDaysWaiting | null | undefined): string | null {
  if (!d) return null;
  const n = `${d.days} day${d.days === 1 ? '' : 's'}`;
  return d.running ? `${n} waiting` : `took ${n}`;
}
